import React, { useEffect, useState, useRef } from "react";
import "./createAssessment.scss";
import {
  Box,
  Button,
  Checkbox,
  createFilterOptions,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  checkSpecialChar,
  createFor,
  eventDateRange,
  extractActiveQuestionCounts,
  isHoliday,
  isInvalidOrZero,
  isValidNumber,
  maxAssessmentCounts,
  regex,
  shouldDisableWeekends,
} from "../../utils/utils";
import ReactQuill from "react-quill";
import SimpleAutoComplete from "../../components/SimpleAutoComplete/SimpleAutoComplete";
import CustomToolbar from "../../components/CustomToolbar/CustomToolbar";
import dayjs from "dayjs";
import DateTimePickerComponent from "../../components/DateTimePickerComponent/DateTimePickerComponent";
import QuestionPoolTable from "../../components/QuestionPool/QuestionPoolTable";
import QuestionTypeTable from "../../components/QuestionType/QuestionTypeTable";
import AutoCompleteTagInput from "../../components/AutoCompleteTagInput/AutoCompleteTagInput";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  addAssessment,
  clearAssessmentInfo,
  clearBootcampInfo,
  clearModuleInfo,
  editAssessment,
  getAssessmentById,
  getBootcampByIdOnAssessment,
  getModuleByIdOnAssessment,
  getModulesList,
  getSessionsList,
} from "../../store/createAssessment";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/Loader/Loader";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import BreadCrumb from "../../components/Breadcrumb/BreadCrumb";
import Constants, { AssessmentTypes } from "../../constants/constants";

var utc = require("dayjs/plugin/utc");
var timezone = require("dayjs/plugin/timezone");

dayjs.extend(utc);
dayjs.extend(timezone);
const tz = "Asia/Kolkata";

const { ACTIVE, SCHEDULED } = Constants;

const initialAssessmentDetails = {
  title: "",
  type: null,
  description: "",
  instruction: "",
  activeFromDate: null,
  activeToDate: null,
  durationInMinutes: 15,
  moduleId: null,
  sessionIds: [],
  markPerEasyQuestion: 1,
  markPerMediumQuestion: 2,
  markPerHardQuestion: 3,
  topicQuestions: null,
};

const hasDetailsChanged = (initialDetails, currentDetails) => {
  const fieldsToCheck = Object.keys(initialDetails);
  return fieldsToCheck.some((field) => {
    if (field === "sessionIds") {
      if (initialDetails[field]?.length !== currentDetails[field]?.length)
        return true;
      const initialIds = initialDetails[field]?.map((item) => item?.id).sort();
      const currentIds = currentDetails[field]?.map((item) => item?.id).sort();
      return !initialIds.every((id, index) => id === currentIds[index]);
    } else if (field === "topicQuestions") {
      const sortedInitialTopicQuestions = initialDetails[field]?.sort(
        (a, b) => a.topicId - b.topicId
      );
      const sortedCurrentTopicQuestions = currentDetails[field]?.sort(
        (a, b) => a.topicId - b.topicId
      );
      return (
        JSON.stringify(sortedInitialTopicQuestions) !==
        JSON.stringify(sortedCurrentTopicQuestions)
      );
    }
    return JSON.stringify(initialDetails) !== JSON.stringify(currentDetails);
  });
};

const validateNumberFields = (label, fieldName) => {
  if (label === "" || label === null) {
    return `${fieldName}`;
  }
  if (label <= 0) {
    return "Must be greater than 0";
  }
  return "";
};

const validateStringField = (field, error) => {
  return field ? "" : `${error}`;
};

const getErrorsForDuration = (isMCQ, durationInMinutes) => {
  return isMCQ
    ? durationInMinutes === "" || durationInMinutes === null
      ? "Duration is required"
      : durationInMinutes < 15
      ? "Must be at least 15 minutes"
      : durationInMinutes > 90
      ? "Must be no more than 90 minutes"
      : ""
    : "";
};

const validateEasyQuestionsCount = (
  easyQuestionsCount,
  estimatedCount,
  topicError
) => {
  if (isValidNumber(easyQuestionsCount)) {
    if (easyQuestionsCount > estimatedCount?.EASY) {
      topicError.easyQuestionsCount = `Count cannot be higher than ${estimatedCount?.EASY}`;
    }
  } else {
    topicError.easyQuestionsCount = "Should contain a valid number";
  }
};

const validateMediumQuestionsCount = (
  mediumQuestionsCount,
  estimatedCount,
  topicError
) => {
  if (isValidNumber(mediumQuestionsCount)) {
    if (mediumQuestionsCount > estimatedCount?.MEDIUM) {
      topicError.mediumQuestionsCount = `Count cannot be higher than ${estimatedCount?.MEDIUM}`;
    }
  } else {
    topicError.mediumQuestionsCount = "Should contain a valid number";
  }
};

const validateHardQuestionsCount = (
  hardQuestionsCount,
  estimatedCount,
  topicError
) => {
  if (isValidNumber(hardQuestionsCount)) {
    if (hardQuestionsCount > estimatedCount?.HARD) {
      topicError.hardQuestionsCount = `Count cannot be higher than ${estimatedCount?.HARD}`;
    }
  } else {
    topicError.hardQuestionsCount = "Should contain a valid number";
  }
};

function getActionButtonText(assessmentId) {
  return <>{assessmentId ? "Update" : "Save"}</>;
}

function getAssessmentTopicQuestions(assessmentId, assessmentDetails) {
  return assessmentId ? assessmentDetails?.topicQuestions : false;
}

function getSessionInputPlaceholder(assessmentDetails) {
  return assessmentDetails?.sessionIds?.length ? null : "Select Session";
}

function getDurationErrorHelperText(durationError, errors) {
  return durationError
    ? "Should only contain number"
    : errors?.durationInMinutes;
}

function getTitleErrorHelperText(
  sessionSpecialChar,
  sessionLengthError,
  errors
) {
  return sessionSpecialChar
    ? "Should not contain special Characters"
    : sessionLengthError
    ? "Maximum 50 characters allowed"
    : errors?.title;
}

function isComponentLoading(
  modulesListLoading,
  assessmentInfoLoading,
  addAssessmentLoader,
  editAssessmentLoader,
  bootcampDataLoading,
  moduleDataLoading
) {
  return (
    modulesListLoading ||
    assessmentInfoLoading ||
    addAssessmentLoader ||
    editAssessmentLoader ||
    bootcampDataLoading ||
    moduleDataLoading
  );
}

function changeForDurationAndMarks(
  errorSetters,
  name,
  value,
  setAssessmentDetails
) {
  errorSetters[name](false);

  if ((value === "" || regex.test(value)) && Number(value) >= 0) {
    setAssessmentDetails((prev) => {
      return {
        ...prev,
        [name]: value === "" ? "" : Number(value),
      };
    });
  } else if (!Number(value)) {
    errorSetters[name](true);
  }
}

function changeForQuestionsCount(
  setErrors,
  topicId,
  name,
  value,
  setAssessmentDetails
) {
  setErrors((prev) => ({
    ...prev,
    topicQuestions: {
      ...(prev.topicQuestions || {}),
      [topicId]: {
        ...(prev.topicQuestions?.[topicId] || {}),
        [name]: false,
        noData: "",
      },
    },
  }));
  const numericValue = Number(value);
  if (!isNaN(numericValue) && numericValue >= 0) {
    setAssessmentDetails((prev) => {
      const updatedTopicQuestions = [...(prev.topicQuestions || [])]; // Create a copy of topicQuestions array
      const topicIndex = updatedTopicQuestions.findIndex(
        (tq) => tq?.topicId === topicId
      );

      if (topicIndex !== -1) {
        updatedTopicQuestions[topicIndex] = {
          ...updatedTopicQuestions[topicIndex],
          [name]: value,
        };
      } else {
        updatedTopicQuestions.push({
          topicId: topicId,
          [name]: value,
        });
      }

      return {
        ...prev,
        topicQuestions: updatedTopicQuestions,
      };
    });
  } else if (!numericValue) {
    setErrors((prev) => ({
      ...prev,
      topicQuestions: {
        ...(prev.topicQuestions || []),
        [topicId]: {
          ...prev.topicQuestions?.[topicId],
          [name]: "Should contain a number only",
        },
      },
    }));
  }
}

function changeForActiveToDate(
  assessmentDetails,
  setAssessmentDetails,
  value,
  name
) {
  if (!assessmentDetails?.activeToDate) {
    setAssessmentDetails((prev) => {
      return {
        ...prev,
        activeToDate: dayjs(value)?.set("hour", 18)?.set("minute", 0),
      };
    });
  } else {
    setAssessmentDetails((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  }
}

function changeForActiveFromDate(
  setErrors,
  value,
  assessmentDetails,
  setAssessmentDetails,
  name
) {
  setErrors((prev) => {
    return {
      ...prev,
      activeToDate: false,
    };
  });
  let newActiveFromDate = value;
  let newActiveToDate = assessmentDetails?.activeToDate;
  if (!assessmentDetails?.activeFromDate) {
    newActiveFromDate = dayjs(value)?.set("hour", 9)?.set("minute", 0);
  }
  if (
    !newActiveToDate ||
    !dayjs(newActiveToDate).isValid() ||
    !dayjs(newActiveToDate).isSame(newActiveFromDate, "date")
  ) {
    newActiveToDate = dayjs(newActiveFromDate)
      ?.set("hour", 18)
      ?.set("minute", 0);
  }
  setAssessmentDetails((prev) => {
    return {
      ...prev,
      [name]: newActiveFromDate,
      sessionIds: assessmentDetails?.sessionIds?.filter((it) =>
        dayjs(it?.endDate).isBefore(dayjs(newActiveFromDate))
      ),
      activeToDate: newActiveToDate,
    };
  });
}

function changeForTitle(
  value,
  setSessionSpecialChar,
  setSessionLengthError,
  setAssessmentDetails,
  name
) {
  if (value?.trim().length <= 50) {
    setSessionSpecialChar(checkSpecialChar(value));
    setSessionLengthError(false);
    setAssessmentDetails((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  } else {
    setSessionLengthError(true);
  }
}

const CreateAssessment = ({
  formBootCampId,
  formModuleId,
  assessmentId,
  viaAssessmentView = false,
  onClose,
}) => {
  const location = useLocation();
  // const { viaAssessmentView } = location?.state || {};
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const viaModule = !!params?.moduleId || !!formModuleId;
  const moduleId = +params?.moduleId || +formModuleId;
  const bootcampId = +params.bootcampId || +formBootCampId;

  const [assessmentDetails, setAssessmentDetails] = useState(
    initialAssessmentDetails
  );
  const [sessionSpecialChar, setSessionSpecialChar] = useState(false);
  const [sessionLengthError, setSessionLengthError] = useState(false);
  const [initialTurn, setInitialTurn] = useState(true);
  const [quillInstruction, setQuillInstruction] = useState("");
  const quillRef = useRef(null);

  const assessmentTypes = useSelector(
    (state) => state?.utility?.constants?.assessmentTypes
  );
  const setInterview = assessmentTypes?.filter(
    (option) => option?.key === AssessmentTypes?.INTERVIEW
  )[0];
  const assessmentData = useSelector((state) => {
    return state?.createAssessment;
  });
  const addAssessmentLoader = assessmentData?.addAssessmentLoader;
  const editAssessmentLoader = assessmentData?.editAssessmentLoader;
  const sessionsList = assessmentData.sessionsListData?.data?.sessions?.results;
  const modulesListLoading = assessmentData.modulesListLoader;
  const bootcampData = assessmentData?.getBootcampDataById?.data;
  const bootcampDataLoading = assessmentData?.bootcampDataLoading;
  const bootcampEndDate = bootcampData?.endDate;
  const moduleDataLoading = assessmentData?.moduleDataLoading;
  const moduleData = assessmentData?.getModuleDataById?.data;
  const assessmentInfo = assessmentData?.assessmentInfo?.data;
  const assessmentStatus = assessmentInfo?.status;
  const isActiveAssessment = assessmentStatus === ACTIVE;
  const assessmentInfoLoading = assessmentData?.getAssessmentLoader;
  const holidaysList = useSelector(
    (state) => state?.utility?.constants?.holidays
  );

  //////////////
  const initialErrorState = {
    title: null,
    type: null,
    description: null,
    instruction: null,
    activeFromDate: null,
    activeToDate: null,
    durationInMinutes: null,
    moduleId: null,
    sessionIds: null,
    markPerEasyQuestion: null,
    markPerMediumQuestion: null,
    markPerHardQuestion: null,
    topicQuestions: null,
  };
  const [errors, setErrors] = useState(initialErrorState);
  const [durationError, setDurationError] = useState(false);
  const [easyError, setEasyError] = useState(false);
  const [mediumError, setMediumError] = useState(false);
  const [HardError, setHardError] = useState(false);
  const [apiError, setApiError] = useState(false);

  const validateFields = () => {
    const {
      title,
      type,
      description,
      instruction,
      activeFromDate,
      activeToDate,
      durationInMinutes,
      moduleId,
      sessionIds,
      markPerEasyQuestion,
      markPerMediumQuestion,
      markPerHardQuestion,
      topicQuestions,
    } = assessmentDetails;
    const isMCQ = assessmentDetails?.type?.key === "MCQ";

    // Check if all counts in a topic are either null or 0
    const newErrors = {
      title: validateStringField(title, "Title is required"),
      type: validateStringField(type, "Type is required"),
      description: validateStringField(description, "Description is required"),
      instruction: validateStringField(instruction, "Instruction is required"),
      activeFromDate: activeFromDate
        ? !isActiveAssessment && dayjs(activeFromDate).isBefore(dayjs())
          ? "Start date and time must be in the future"
          : ""
        : "Start Date is required",
      activeToDate: activeToDate
        ? !dayjs(activeToDate)?.isAfter(dayjs(activeFromDate)) ||
          dayjs(activeToDate)?.isBefore(dayjs())
          ? "End Date must be after Start Date"
          : ""
        : "End Date is required",
      durationInMinutes: getErrorsForDuration(isMCQ, durationInMinutes),
      markPerEasyQuestion: validateNumberFields(
        markPerEasyQuestion,
        "Score is required"
      ),
      markPerMediumQuestion: validateNumberFields(
        markPerMediumQuestion,
        "Score is required"
      ),
      markPerHardQuestion: validateNumberFields(
        markPerHardQuestion,
        "Score is required"
      ),
      moduleId: viaModule && !moduleId ? "module is required" : "",
      sessionIds:
        sessionIds && sessionIds.length > 0 ? "" : "session is required",
    };
    const topicErrors = topicQuestions?.reduce((acc, topic) => {
      const {
        topicId,
        easyQuestionsCount,
        mediumQuestionsCount,
        hardQuestionsCount,
      } = topic;

      const extractedQuestionCounts =
        extractActiveQuestionCounts(assessmentDetails);
      const estimatedCount = maxAssessmentCounts(
        assessmentDetails?.type?.key,
        extractedQuestionCounts,
        topicId
      );

      let topicError = {};
      if (
        isInvalidOrZero(easyQuestionsCount) &&
        isInvalidOrZero(mediumQuestionsCount) &&
        isInvalidOrZero(hardQuestionsCount)
      ) {
        topicError.noData = `At least one question count is required`;
      } else {
        validateEasyQuestionsCount(
          easyQuestionsCount,
          estimatedCount,
          topicError
        );
        validateMediumQuestionsCount(
          mediumQuestionsCount,
          estimatedCount,
          topicError
        );
        validateHardQuestionsCount(
          hardQuestionsCount,
          estimatedCount,
          topicError
        );
      }

      if (Object.keys(topicError).length > 0) {
        acc[topicId] = topicError; // Store the error by topicId
      }

      return acc;
    }, {});

    newErrors.topicQuestions =
      Object.keys(topicErrors).length > 0 ? topicErrors : "";
    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === "");
  };
  const errorSetters = {
    durationInMinutes: setDurationError,
    markPerHardQuestion: setHardError,
    markPerMediumQuestion: setMediumError,
    markPerEasyQuestion: setEasyError,
  };
  const handleAssessmentDetailsChange = async (
    name,
    value,
    topicId = null,
    assessmentQuestions = null
  ) => {
    const changeForType = () => {
      if (name === "type") {
        setErrors((prev) => {
          return {
            ...prev,
            topicQuestions: null,
          };
        });
      }
    };

    setApiError(false);
    setErrors((prev) => {
      return {
        ...prev,
        [name]: false,
      };
    });

    changeForType();

    if (name === "title") {
      changeForTitle(
        value,
        setSessionSpecialChar,
        setSessionLengthError,
        setAssessmentDetails,
        name
      );
    } else if (name === "activeFromDate") {
      changeForActiveFromDate(
        setErrors,
        value,
        assessmentDetails,
        setAssessmentDetails,
        name
      );
    } else if (name === "activeToDate") {
      changeForActiveToDate(
        assessmentDetails,
        setAssessmentDetails,
        value,
        name
      );
    } else if (
      [
        "easyQuestionsCount",
        "mediumQuestionsCount",
        "hardQuestionsCount",
      ].includes(name)
    ) {
      changeForQuestionsCount(
        setErrors,
        topicId,
        name,
        value,
        setAssessmentDetails
      );
    } else if (
      [
        "durationInMinutes",
        "markPerHardQuestion",
        "markPerMediumQuestion",
        "markPerEasyQuestion",
      ].includes(name)
    ) {
      changeForDurationAndMarks(
        errorSetters,
        name,
        value,
        setAssessmentDetails
      );
    } else if (name === "instruction") {
      setQuillInstruction(value);
      setAssessmentDetails((prev) => {
        return {
          ...prev,
          [name]: value,
        };
      });
    } else {
      setAssessmentDetails((prev) => {
        return {
          ...prev,
          [name]: value,
        };
      });
    }
  };
  const getNavigationPath = () => {
    onClose();
  };
  const handleAssessmentCancel = () => {
    if (assessmentDetailsChanged) {
      setCancelModalOpen(true);
    } else {
      navigate(getNavigationPath());
    }
  };

  const getSessionIds = (assessmentDetails) => {
    return assessmentDetails?.sessionIds?.map((session) => session?.id) || [];
  };

  const getActiveFromDate = (assessmentDetails) => {
    return assessmentDetails?.activeFromDate != null &&
      assessmentDetails?.activeFromDate.$d != "Invalid Date"
      ? dayjs(assessmentDetails?.activeFromDate)?.tz(tz)?.format()
      : null;
  };

  const getActiveToDate = (assessmentDetails) => {
    return assessmentDetails?.activeToDate != null &&
      assessmentDetails?.activeToDate.$d != "Invalid Date"
      ? dayjs(assessmentDetails?.activeToDate)?.tz(tz)?.format()
      : null;
  };

  const getDuration = (assessmentDetails) => {
    return assessmentDetails?.durationInMinutes
      ? assessmentDetails?.durationInMinutes
      : 0;
  };

  const getAssessmentApiCall = async (assessmentId, payload) => {
    return !assessmentId
      ? await dispatch(addAssessment(payload))
      : await dispatch(editAssessment(payload));
  };

  const handleAssessmentSubmit = async () => {
    if (validateFields()) {
      let apiValue;
      const payload = {
        ...assessmentDetails,
        type: assessmentDetails?.type?.key,
        ...(viaModule
          ? { moduleId: assessmentDetails?.moduleId?.id }
          : { bootcampId }),
        ...(assessmentId ? { assessmentId: assessmentId } : {}),
        sessionIds: getSessionIds(assessmentDetails),
        activeFromDate: getActiveFromDate(assessmentDetails),
        activeToDate: getActiveToDate(assessmentDetails),
        durationInMinutes: getDuration(assessmentDetails),
      };
      apiValue = await getAssessmentApiCall(assessmentId, payload);

      if (apiValue?.error?.message === "Rejected") {
        setApiError(true);
      } else {
        navigate(getNavigationPath());
        onClose();
      }
    }
  };

  //////////////
  const isEvaluationActive = assessmentInfo?.permission?.isEvaluationActive;
  ///////////////
  useEffect(() => {
    if (assessmentId) {
      dispatch(getAssessmentById(assessmentId));
    }
    dispatch(
      getBootcampByIdOnAssessment({
        bootcampId,
      })
    );
    if (viaModule) {
      dispatch(getSessionsList(moduleId));
      dispatch(getModuleByIdOnAssessment({ bootcampId, moduleId }));
    } else {
      dispatch(getModulesList(bootcampId));
    }

    return () => {
      setAssessmentDetails(initialAssessmentDetails);
      setQuillInstruction("");
      dispatch(clearAssessmentInfo());
      dispatch(clearBootcampInfo());
      dispatch(clearModuleInfo());
    };
  }, []);
  useEffect(() => {
    const mapAssessmentDetails = (info) => ({
      title: info?.title,
      type: assessmentTypes?.find((item) => item?.key === info?.type),
      description: info?.description,
      instruction: info?.instruction || "",
      activeFromDate: dayjs(info?.activeFromDate).isValid()
        ? dayjs(info?.activeFromDate)
        : null,
      activeToDate: dayjs(info?.activeToDate).isValid()
        ? dayjs(info?.activeToDate)
        : null,
      durationInMinutes: info?.durationInMinutes,
      moduleId: info?.module,
      sessionIds: info?.sessions?.results || [],
      markPerEasyQuestion: info?.markPerEasyQuestion,
      markPerMediumQuestion: info?.markPerMediumQuestion,
      markPerHardQuestion: info?.markPerHardQuestion,
      topicQuestions: info?.assessmentTopicQuestions?.results?.map((item) => ({
        topicId: item?.topic?.id,
        name: item?.topic?.name,
        easyQuestionsCount: item?.easyQuestionsCount,
        mediumQuestionsCount: item?.mediumQuestionsCount,
        hardQuestionsCount: item?.hardQuestionsCount,
        assessmentQuestions: item?.topic?.activeAssessmentQuestionCount,
      })),
    });
    if (assessmentInfo && assessmentTypes) {
      const details = mapAssessmentDetails(assessmentInfo);
      setAssessmentDetails(details);
      setInitialFormDetails(details);
      // Initialize ReactQuill with instruction value when data loads
      setQuillInstruction(details.instruction || "");
    }
  }, [assessmentInfo, assessmentTypes]);

  // Update ReactQuill content when quillInstruction changes (only for programmatic updates)
  useEffect(() => {
    const hasRequirements =
      quillRef.current &&
      quillRef.current.getEditor &&
      quillInstruction !== undefined &&
      assessmentInfo;

    if (!hasRequirements) {
      return;
    }

    const editor = quillRef.current.getEditor();
    if (!editor || !editor.root) {
      return;
    }

    const currentContent = editor.root.innerHTML.trim();
    const newContent = (quillInstruction || "").trim();
    const shouldUpdate = currentContent !== newContent && newContent !== "";

    if (shouldUpdate) {
      const timeoutId = setTimeout(() => {
        if (editor && editor.root) {
          const delta = editor.clipboard.convert({ html: newContent });
          editor.setContents(delta, "silent");
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [quillInstruction, assessmentInfo]);

  /////////////////////////////
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const handleCancel = () => {
    setCancelModalOpen(false);
  };

  const handleNavigation = async () => {
    setCancelModalOpen(false);
    navigate(getNavigationPath());
  };
  /////////////////////////////

  useEffect(() => {
    const updateDetails = () => ({
      ...assessmentDetails,
      type: setInterview,
      sessionIds: sessionsList?.filter((it) =>
        dayjs(it?.endDate).isBefore(
          dayjs(bootcampEndDate)?.set("hour", 9)?.set("minute", 0)
        )
      ),
      activeFromDate: dayjs(bootcampEndDate)?.set("hour", 9)?.set("minute", 0),
      activeToDate: dayjs(bootcampEndDate)?.set("hour", 18)?.set("minute", 0),
    });
    if (!viaModule && !assessmentId && sessionsList && setInterview) {
      setAssessmentDetails(updateDetails());
      setInitialFormDetails(updateDetails());
    }
  }, [bootcampEndDate, sessionsList, setInterview]);

  useEffect(() => {
    const updateDetails = () => ({
      ...assessmentDetails,
      type: setInterview,
      sessionIds: sessionsList?.filter((it) =>
        dayjs(it?.endDate).isBefore(
          dayjs(moduleData?.endDate)?.set("hour", 9)?.set("minute", 0)
        )
      ),
      ...(viaModule && {
        moduleId: moduleData,
        activeFromDate: dayjs(moduleData?.endDate)
          ?.set("hour", 9)
          ?.set("minute", 0),
        activeToDate: dayjs(moduleData?.endDate)
          ?.set("hour", 18)
          ?.set("minute", 0),
      }),
    });

    if (sessionsList && !assessmentId && moduleData && setInterview) {
      setAssessmentDetails((prev) => ({
        ...prev,
        ...updateDetails(),
      }));

      setInitialFormDetails(updateDetails());
    }
  }, [sessionsList, moduleData, setInterview]);

  useEffect(() => {
    if (!assessmentDetails?.type?.key) return;

    const defaultDuration =
      assessmentDetails.type.key === "INTERVIEW"
        ? ""
        : assessmentDetails?.durationInMinutes
        ? assessmentDetails?.durationInMinutes
        : 15;
    setAssessmentDetails((prev) => {
      return {
        ...prev,
        durationInMinutes: defaultDuration,
      };
    });
    if (initialTurn) {
      setInitialFormDetails((prev) => {
        return {
          ...prev,
          durationInMinutes: defaultDuration,
        };
      });
      setInitialTurn(false);
    }
  }, [assessmentDetails?.type?.key]);
  /////////////////////// To disable update button if data isn't changed ///////////////////////
  const [initialFormDetails, setInitialFormDetails] =
    useState(assessmentDetails);
  const assessmentDetailsChanged = hasDetailsChanged(
    initialFormDetails,
    assessmentDetails
  );

  /////////////////////// To get sessions of before date from assessment start date ///////////////////////
  const sessions = sessionsList?.filter((it) =>
    dayjs(it?.endDate).isBefore(dayjs(assessmentDetails?.activeFromDate))
  );

  //------------------------------ Breadcrumbs ------------------------------//
  const breadcrumbs = () => {
    return moduleId
      ? [
          { label: "Home", url: "/" },
          {
            label: bootcampData?.name ? bootcampData?.name : "Bootcamps",
            url: "/",
            isBootcamp: true,
            bootcampId: bootcampId,
          },
          {
            label: moduleData?.name ? moduleData?.name : "Module",
            url: `/bootcamp/${bootcampId}/module/${moduleData?.id}` || "/",
          },
          {
            label: "Assessments",
            url: `/bootcamp/${bootcampId}/module/${moduleData?.id}/assessments`,
          },
          ...(assessmentInfo?.title ? [{ label: assessmentInfo?.title }] : []),
        ]
      : [
          { label: "Home", url: "/" },
          {
            label: bootcampData?.name ? bootcampData?.name : "Bootcamps",
            url: "/",
            isBootcamp: true,
            bootcampId: bootcampId,
          },
          {
            label: "Assessments",
            url: `/bootcamp/${bootcampId}/assessments`,
          },
          ...(assessmentInfo?.title ? [{ label: assessmentInfo?.title }] : []),
        ];
  };

  if (
    isComponentLoading(
      modulesListLoading,
      assessmentInfoLoading,
      addAssessmentLoader,
      editAssessmentLoader,
      bootcampDataLoading,
      moduleDataLoading
    )
  )
    return <Loader />;
  return (
    <Grid container className="main-container">
      <BreadCrumb breadcrumbs={breadcrumbs()} />

      <Grid item className="header-container" md={12}>
        <Typography variant="font_21_bold">
          {assessmentId ? "Edit Assessment" : "Create Assessment"}
        </Typography>
      </Grid>

      <Grid item md={12} className="body-container">
        <Grid container className="table-parent-container">
          <Paper elevation={0} className="assessment-paper-container">
            <Grid className="assessment-fields">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "0.75rem",
                }}
              >
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "20%",
                    marginRight: "1rem",
                  }}
                >
                  <SimpleAutoComplete
                    autoFocus
                    label="Create For"
                    placeholder="Select Option"
                    options={createFor}
                    getOptionLabel={(option) => option?.value}
                    value={viaModule ? createFor[1] : createFor[0]}
                    disabled={true}
                    renderOption={(props, option, value) => {
                      return (
                        <li
                          {...props}
                          key={props?.id}
                          className={
                            option?.value ===
                            assessmentDetails?.createFor?.value
                              ? "multi-tag-edit-list-selected"
                              : "multi-tag-style"
                          }
                        >
                          <Box lineHeight={1.1}>
                            <Box>{`${option?.value}`}</Box>
                          </Box>
                        </li>
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "59%",
                    marginRight: "1rem",
                  }}
                >
                  <TextField
                    disabled={isActiveAssessment}
                    size="small"
                    label="Title"
                    color="secondary"
                    fullWidth
                    className="filter-inputs"
                    margin="dense"
                    variant="outlined"
                    value={assessmentDetails?.title}
                    onChange={(e) =>
                      handleAssessmentDetailsChange("title", e?.target?.value)
                    }
                    error={
                      !!errors?.title ||
                      apiError ||
                      sessionSpecialChar ||
                      sessionLengthError
                    }
                    helperText={getTitleErrorHelperText(
                      sessionSpecialChar,
                      sessionLengthError,
                      errors
                    )}
                    required={true}
                  />
                </Box>
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "20%",
                  }}
                >
                  <SimpleAutoComplete
                    disabled={isActiveAssessment || !!assessmentId}
                    label="Type"
                    placeholder="Select Assessment Type"
                    options={assessmentTypes}
                    onChange={(event, newValue) =>
                      handleAssessmentDetailsChange("type", newValue)
                    }
                    getOptionLabel={(option) => option?.value || ""}
                    value={assessmentDetails?.type}
                    error={!!errors?.type || apiError}
                    helperText={errors?.type}
                    required={true}
                    renderOption={(props, option, value) => {
                      return (
                        <li
                          {...props}
                          key={props?.key}
                          className={
                            option?.key === assessmentDetails?.type?.key
                              ? "multi-tag-edit-list-selected"
                              : "multi-tag-style"
                          }
                        >
                          <Box lineHeight={1.1}>
                            <Box>{`${option?.value}`}</Box>
                          </Box>
                        </li>
                      );
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid className="assessment-fields">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "0.75rem",
                }}
              >
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "50%",
                    marginRight: "1rem",
                  }}
                >
                  <TextField
                    multiline
                    minRows={9}
                    label="Description"
                    color="secondary"
                    fullWidth
                    className="filter-inputs"
                    variant="outlined"
                    size="small"
                    margin="dense"
                    value={assessmentDetails?.description}
                    onChange={(e) =>
                      handleAssessmentDetailsChange(
                        "description",
                        e?.target?.value
                      )
                    }
                    required={true}
                    error={!!errors?.description || apiError}
                    helperText={errors?.description}
                  />
                </Box>
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "50%",
                  }}
                >
                  <Box sx={{ marginBottom: "0.75rem", marginTop: "-0.5rem" }}>
                    <Typography
                      color={
                        !!errors.instruction || apiError ? "error" : "offline"
                      }
                      variant="font_14"
                      sx={{ marginBottom: "1rem" }}
                    >
                      Instructions *
                    </Typography>
                  </Box>
                  <CustomToolbar />
                  <ReactQuill
                    ref={quillRef}
                    key={`quill-${
                      assessmentInfo?.id || assessmentId || "new"
                    }-${assessmentInfo ? "loaded" : "init"}`}
                    className="session-quill"
                    theme="snow"
                    value={quillInstruction}
                    onChange={(value) =>
                      handleAssessmentDetailsChange("instruction", value)
                    }
                    modules={{
                      toolbar: {
                        container: "#toolbar",
                      },
                    }}
                    placeholder="Enter Text"
                    bounds={".session-quill"}
                  />
                  {!!errors.instruction || apiError ? (
                    <Typography
                      sx={{ paddingLeft: "1rem", marginBottom: "1rem" }}
                      variant="font_12"
                      color="error"
                    >
                      {errors?.instruction}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Grid>

            <Grid className="assessment-fields">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "0.75rem",
                }}
              >
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "33%",
                    marginRight: "1rem",
                  }}
                >
                  <DateTimePickerComponent
                    disabled={isActiveAssessment || isEvaluationActive}
                    label="Start Date"
                    format="DD/MM/YYYY hh:mm A"
                    onChange={(e) =>
                      handleAssessmentDetailsChange("activeFromDate", e)
                    }
                    value={assessmentDetails?.activeFromDate}
                    views={["year", "day", "hours", "minutes"]}
                    error={!!errors?.activeFromDate || apiError}
                    helperText={errors?.activeFromDate}
                    classList="margin-right-2"
                    required={true}
                    minDateTime={dayjs()}
                    minDate={dayjs().startOf("day").add(1, "day")}
                    shouldDisableDate={(date) => {
                      return (
                        shouldDisableWeekends(date) ||
                        isHoliday(date, holidaysList) ||
                        (viaModule
                          ? !eventDateRange(date, assessmentDetails?.moduleId)
                          : dayjs(date)
                              .tz()
                              .isBefore(
                                dayjs(bootcampData?.startDate)
                                  .tz()
                                  .startOf("day")
                              ))
                      );
                    }}
                    closeOnSelect={false}
                    disablePast={true}
                  />
                </Box>
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "33%",
                    marginRight: "1rem",
                  }}
                >
                  <DateTimePickerComponent
                    label="End Date"
                    format="DD/MM/YYYY hh:mm A"
                    onChange={(e) => {
                      handleAssessmentDetailsChange("activeToDate", e);
                    }}
                    value={assessmentDetails?.activeToDate}
                    required={true}
                    error={!!errors.activeToDate || apiError}
                    helperText={errors?.activeToDate}
                    views={["year", "day", "hours", "minutes"]}
                    minDateTime={dayjs(assessmentDetails?.activeFromDate).add(
                      1,
                      "hour"
                    )}
                    disabled={
                      !assessmentDetails?.activeFromDate || isEvaluationActive
                    }
                    shouldDisableDate={(date) => {
                      return (
                        shouldDisableWeekends(date) ||
                        isHoliday(date, holidaysList) ||
                        (viaModule
                          ? !eventDateRange(date, assessmentDetails?.moduleId)
                          : dayjs(date)
                              .tz()
                              .isBefore(
                                dayjs(bootcampData?.startDate)
                                  .tz()
                                  .startOf("day")
                              ))
                      );
                    }}
                    closeOnSelect={false}
                    disablePast={true}
                  />
                </Box>
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "33%",
                  }}
                >
                  <TextField
                    disabled={
                      assessmentDetails?.type?.key === "INTERVIEW" ||
                      isEvaluationActive
                    }
                    size="small"
                    label="Duration (in minutes)"
                    color="secondary"
                    fullWidth
                    className="filter-inputs"
                    margin="dense"
                    variant="outlined"
                    value={assessmentDetails?.durationInMinutes}
                    onChange={(e) =>
                      handleAssessmentDetailsChange(
                        "durationInMinutes",
                        e?.target?.value
                      )
                    }
                    error={
                      !!errors?.durationInMinutes || durationError || apiError
                    }
                    helperText={getDurationErrorHelperText(
                      durationError,
                      errors
                    )}
                    required={true}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid className="assessment-fields">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  marginBottom: "0.75rem",
                }}
              >
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "50%",
                    marginRight: "1rem",
                  }}
                >
                  <SimpleAutoComplete
                    label="Module"
                    placeholder="Select Module"
                    disabled
                    onChange={(event, newValue) =>
                      handleAssessmentDetailsChange("moduleId", newValue)
                    }
                    required={true}
                    error={!!errors?.moduleId || apiError}
                    helperText={errors?.moduleId}
                    getOptionLabel={(option) => option?.name}
                    value={assessmentDetails?.moduleId}
                    renderOption={(props, option, value) => {
                      return (
                        <li
                          {...props}
                          key={props?.id}
                          className={
                            option?.id === assessmentDetails?.moduleId?.id
                              ? "multi-tag-edit-list-selected"
                              : "multi-tag-style"
                          }
                        >
                          <Box className="flex-options">
                            <Box>{option?.name}</Box>
                          </Box>
                        </li>
                      );
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    marginBottom: "0.75rem",
                    flexBasis: "50%",
                  }}
                >
                  <AutoCompleteTagInput
                    disabled={
                      assessmentStatus && assessmentStatus !== SCHEDULED
                    }
                    label="Sessions"
                    required={true}
                    placeholder={getSessionInputPlaceholder(assessmentDetails)}
                    options={sessions}
                    selectedValue={assessmentDetails?.sessionIds}
                    getOptionLabel={(option) => option?.name}
                    filterOptions={(options, params) => {
                      // <<<--- inject the Select All option
                      const filter = createFilterOptions();
                      const filtered = filter(options, params);
                      if (sessions?.length === 0) {
                        return [{ name: "No options", all: false }];
                      }
                      return [{ name: "Select All", all: true }, ...filtered];
                    }}
                    onChange={(event, newValue) => {
                      const idCounts = newValue.reduce((counts, { id }) => {
                        counts[id] = (counts[id] || 0) + 1;
                        return counts;
                      }, {});
                      const uniqueNewValue = newValue.filter(({ id }) => {
                        return idCounts[id] === 1;
                      });
                      if (newValue?.find((option) => option.all)) {
                        return handleAssessmentDetailsChange(
                          "sessionIds",
                          assessmentDetails?.sessionIds?.length ===
                            sessions?.length
                            ? []
                            : sessions
                        );
                      }
                      handleAssessmentDetailsChange(
                        "sessionIds",
                        uniqueNewValue
                      );
                    }}
                    renderOption={(props, option, value) => {
                      // Check if option is "No options" and render accordingly
                      if (option.name === "No options") {
                        return (
                          <Box className="auto-complete-no-options">
                            <Box>{option?.name}</Box>
                          </Box>
                        );
                      }
                      const optionChecked =
                        (option?.all &&
                          assessmentDetails?.sessionIds?.length ===
                            sessions?.length) ||
                        assessmentDetails?.sessionIds?.find(
                          (e) => e?.id === option?.id
                        ) != null;

                      return (
                        <li
                          {...props}
                          key={props?.id}
                          className={
                            optionChecked
                              ? "multi-tag-edit-list-selected"
                              : "multi-tag-style"
                          }
                        >
                          <Checkbox
                            className="auto-complete-checkbox-list"
                            size="small"
                            checked={optionChecked}
                            color="secondary"
                          />
                          <Box className="flex-options">
                            <Box>{option?.name}</Box>
                            {option?.name === "Select All" ? null : (
                              <Box>
                                {dayjs(option?.startDate)?.format("DD/MM/YYYY")}
                              </Box>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                    error={!!errors?.sessionIds || apiError}
                    helperText={errors?.sessionIds}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid className="assessment-fields">
              <Box
                sx={{
                  marginBottom: "0.75rem",
                }}
              >
                <Box sx={{ marginBottom: "0.75rem" }}>
                  <Typography variant="font_14">
                    Topic-wise Question Count Per Question Type
                  </Typography>
                </Box>
                <QuestionPoolTable
                  selectedSessions={assessmentDetails?.sessionIds || []}
                  handleAssessmentDetailsChange={handleAssessmentDetailsChange}
                  assessmentTopicQuestions={getAssessmentTopicQuestions(
                    assessmentId,
                    assessmentDetails
                  )}
                  errors={errors}
                  apiError={apiError}
                  assessmentDetails={assessmentDetails}
                  setAssessmentDetails={setAssessmentDetails}
                  setInitialFormDetails={setInitialFormDetails}
                  isEvaluationActive={isEvaluationActive}
                />
              </Box>
            </Grid>

            <Grid className="assessment-fields">
              <Box
                sx={{
                  marginBottom: "0.75rem",
                }}
              >
                <Box sx={{ marginBottom: "0.75rem" }}>
                  <Typography variant="font_14">
                    Score Per Question Type
                  </Typography>
                </Box>
                <QuestionTypeTable
                  selectedSessions={assessmentDetails?.sessionIds}
                  handleAssessmentDetailsChange={handleAssessmentDetailsChange}
                  errors={errors}
                  assessmentDetails={assessmentDetails}
                  apiError={apiError}
                  easyError={easyError}
                  mediumError={mediumError}
                  HardError={HardError}
                  isEvaluationActive={isEvaluationActive}
                />
              </Box>
            </Grid>
            <Grid
              sx={{
                padding: "1.875rem 0 1.625rem",
                display: "flex",
                width: "26.265rem",
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleAssessmentCancel}
                className="block-button margin-right-2"
              >
                <Typography variant="outlineBtnLabel">Cancel</Typography>
              </Button>
              <Button
                onClick={handleAssessmentSubmit}
                variant="contained"
                color="secondary"
                className="block-button"
                disabled={!assessmentDetailsChanged}
              >
                <Typography variant="outlineBtnLabel">
                  {getActionButtonText(assessmentId)}
                </Typography>
              </Button>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <DeleteModal
        open={cancelModalOpen}
        title="Are you sure? Canceling will discard all the data you've entered"
        handleClose={handleCancel}
        handleDelete={handleNavigation}
        buttonText="Yes"
        width={"21.7rem"}
      />
    </Grid>
  );
};

export default CreateAssessment;
